/**
 * Texto e dados estruturados gerados do perfil público (`/@slug`), do serviço
 * (`/@slug/service/:id`), do profissional da equipe
 * (`/@slug/professional/:id`) e do diretório (`/profissionais/...`).
 *
 * Puro (sem React, só imports relativos): a função da Vercel
 * (`api/profile-page.ts`, `api/directory-page.ts`, `api/og-image.ts`) e as
 * telas importam daqui. Assim o HTML que o buscador lê antes do JavaScript e a
 * tela que a pessoa vê dizem a mesma coisa, com o mesmo título.
 */
import { formatBRL } from '../../content/facts';
import { DESCRIPTION_MAX, DESCRIPTION_MIN, pageTitle, TITLE_MAX } from './meta';

// ---------- tipos (o mínimo que as duas pontas têm) ----------

export interface MetaService {
  id: string;
  name: string;
  description?: string | null;
  price?: number | string | null;
  priceDisplayMode?: string | null;
  durationMinutes?: number | null;
  /** Assinatura: o preço é do ciclo (`MONTHLY` = por mês). */
  billingInterval?: string | null;
  catalogItemId?: string | null;
}

export interface MetaTeamMember {
  providerId: string;
  name: string;
  bio?: string | null;
  image?: string | null;
  whatsappNumber?: string | null;
  services: MetaService[];
}

export interface MetaCatalogItem {
  id: string;
  name: string;
  description?: string | null;
  durationMinutes?: number | null;
  professionals: {
    providerId: string;
    serviceId: string;
    price: number | string | null;
    priceDisplayMode: string | null;
    durationMinutes: number | null;
    billingInterval?: string | null;
  }[];
}

export interface OpeningInterval {
  /** 0 = domingo. */
  dayOfWeek: number;
  opens: string;
  closes: string;
}

export interface MetaProfile {
  slug: string;
  businessName: string;
  bio?: string | null;
  category?: string | null;
  categoryLabel?: string | null;
  categorySlug?: string | null;
  activityLabel?: string | null;
  activityPackSlug?: string | null;
  city?: string | null;
  state?: string | null;
  whatsappNumber?: string | null;
  logoUrl?: string | null;
  avatarUrl?: string | null;
  coverUrl?: string | null;
  gallery?: { url: string; providerId?: string | null }[] | null;
  services?: MetaService[] | null;
  catalog?: MetaCatalogItem[] | null;
  team?: MetaTeamMember[] | null;
  info?: {
    businessHours?: string | null;
    openingHours?: OpeningInterval[] | null;
    address?: string | null;
    paymentMethods?: string[] | null;
    socialLinks?: { instagram?: string; website?: string } | null;
  } | null;
  booking?: { minCancelLeadMinutes: number } | null;
}

export interface PageMeta {
  title: string;
  description: string;
  /** Caminho com "/" na frente; vira a canônica. */
  path: string;
}

export interface FaqItem {
  question: string;
  answer: string;
}

// ---------- preço ----------

export interface VisiblePrice {
  value: number;
  /** "a partir de". */
  from: boolean;
  /** Assinatura: o valor é do ciclo. */
  interval: string | null;
}

/**
 * O preço que o visitante vê, ou `null`. Única regra para card, texto e
 * dados estruturados: preço oculto (`hidden`), vazio, zero ou negativo não
 * existe (antes `Number(null)` virava `"price": 0`).
 */
export function visiblePrice(
  service: Pick<MetaService, 'price' | 'priceDisplayMode' | 'billingInterval'>,
): VisiblePrice | null {
  if (service.priceDisplayMode === 'hidden') return null;
  if (service.price === null || service.price === undefined || service.price === '') return null;
  const value = typeof service.price === 'number' ? service.price : Number(service.price);
  if (!Number.isFinite(value) || value <= 0) return null;
  return {
    value,
    from: service.priceDisplayMode === 'from',
    interval: service.billingInterval ?? null,
  };
}

const INTERVAL_TEXT: Record<string, string> = {
  WEEKLY: 'por semana',
  MONTHLY: 'por mês',
  BIMONTHLY: 'a cada 2 meses',
  TRIMESTERLY: 'a cada 3 meses',
  SEMIANNUALLY: 'a cada 6 meses',
  YEARLY: 'por ano',
};

/** "R$ 45,00", "a partir de R$ 30,00", "R$ 120,00 por mês"; `null` sem preço visível. */
export function priceText(
  service: Pick<MetaService, 'price' | 'priceDisplayMode' | 'billingInterval'>,
): string | null {
  const price = visiblePrice(service);
  if (!price) return null;
  const money = formatBRL(price.value);
  const base = price.from ? `a partir de ${money}` : money;
  const interval = price.interval ? INTERVAL_TEXT[price.interval] : null;
  return interval ? `${base} ${interval}` : base;
}

/** "40 min", "1h", "1h30". */
export function durationText(minutes: number | null | undefined): string | null {
  if (!minutes || minutes <= 0) return null;
  if (minutes < 60) return `${minutes} min`;
  const rest = minutes % 60;
  return rest ? `${Math.floor(minutes / 60)}h${String(rest).padStart(2, '0')}` : `${minutes / 60}h`;
}

// ---------- dados derivados do perfil ----------

const PAYMENT_LABELS: Record<string, string> = {
  PIX: 'Pix',
  CASH: 'Dinheiro',
  CREDIT_CARD: 'Cartão de crédito',
  BANK_SLIP: 'Boleto',
};

/** Rótulo da forma de pagamento como a tela mostra ("Cartão de crédito"). */
export function paymentMethodLabel(value: string): string {
  return PAYMENT_LABELS[value?.toUpperCase()] ?? value;
}

/** Formas de pagamento no meio da frase: "Pix, dinheiro e cartão de crédito". */
function paymentMethodsInline(methods: string[]): string {
  return joinPt(
    methods.map((method) => {
      const label = paymentMethodLabel(method);
      return label === 'Pix' ? label : label.charAt(0).toLowerCase() + label.slice(1);
    }),
  );
}

function joinPt(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} e ${items[items.length - 1]}`;
}

/** Ramo como a tela mostra: o rótulo do prestador, o do pacote ou o do catálogo. */
export function profileBranch(profile: MetaProfile): string | null {
  return (
    profile.category?.trim() ||
    profile.activityLabel?.trim() ||
    profile.categoryLabel?.trim() ||
    null
  );
}

/** "São Paulo (SP)". Só cidade e UF, nunca a rua (D6). */
export function profilePlace(profile: Pick<MetaProfile, 'city' | 'state'>): string | null {
  const city = profile.city?.trim();
  if (!city) return null;
  return profile.state ? `${city} (${profile.state})` : city;
}

/**
 * Os serviços da página numa lista só: os do prestador; na equipe, um por
 * item do catálogo (menor preço, "a partir de" quando os valores diferem); sem
 * catálogo, os de cada profissional sem repetir o mesmo serviço.
 */
export function profileServices(profile: MetaProfile): MetaService[] {
  if (profile.services?.length) return profile.services;
  if (profile.catalog?.length) {
    return profile.catalog.flatMap((item) => {
      if (!item.professionals.length) return [];
      const visible = item.professionals
        .map((p) => ({ p, price: visiblePrice(p) }))
        .filter(
          (entry): entry is { p: (typeof item.professionals)[number]; price: VisiblePrice } =>
            !!entry.price,
        );
      const cheapest = visible.sort((a, b) => a.price.value - b.price.value)[0];
      const sameValue = new Set(visible.map((entry) => entry.price.value)).size <= 1;
      const first = item.professionals[0];
      return [
        {
          id: first.serviceId,
          name: item.name,
          description: item.description ?? null,
          price: cheapest ? cheapest.price.value : null,
          priceDisplayMode: cheapest
            ? sameValue
              ? cheapest.p.priceDisplayMode
              : 'from'
            : 'hidden',
          durationMinutes: first.durationMinutes ?? item.durationMinutes ?? null,
          billingInterval: cheapest?.p.billingInterval ?? null,
          catalogItemId: item.id,
        },
      ];
    });
  }
  const seen = new Set<string>();
  const list: MetaService[] = [];
  for (const member of profile.team ?? []) {
    for (const service of member.services) {
      const key = service.catalogItemId ?? service.id;
      if (seen.has(key)) continue;
      seen.add(key);
      list.push(service);
    }
  }
  return list;
}

/** Algum serviço tem agendamento online (tem duração). */
export function hasBookableService(services: MetaService[]): boolean {
  return services.some((service) => service.durationMinutes != null && service.durationMinutes > 0);
}

/** Menor preço de um atendimento (assinatura fica fora: o valor dela é do ciclo). */
export function lowestPrice(services: MetaService[]): number | null {
  const values = services
    .filter((service) => !service.billingInterval)
    .map((service) => visiblePrice(service)?.value)
    .filter((value): value is number => value !== undefined);
  return values.length ? Math.min(...values) : null;
}

const DAY_NAMES = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];
/** A semana começa na segunda, como na tela. */
const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];

/** "de segunda a sexta e sábado", "segunda, quarta e sexta". */
export function attendanceDays(intervals: OpeningInterval[] | null | undefined): string | null {
  const days = WEEK_ORDER.filter((day) => (intervals ?? []).some((i) => i.dayOfWeek === day));
  if (!days.length) return null;
  const runs: number[][] = [];
  for (const day of days) {
    const last = runs[runs.length - 1];
    if (last && WEEK_ORDER.indexOf(day) === WEEK_ORDER.indexOf(last[last.length - 1]) + 1)
      last.push(day);
    else runs.push([day]);
  }
  const parts = runs.flatMap((run) =>
    run.length >= 3
      ? [`de ${DAY_NAMES[run[0]]} a ${DAY_NAMES[run[run.length - 1]]}`]
      : run.map((day) => DAY_NAMES[day]),
  );
  return joinPt(parts);
}

/** "24 horas", "2 horas", "30 minutos", "2 dias". */
export function leadTimeText(minutes: number): string {
  if (minutes >= 2880 && minutes % 1440 === 0) return `${minutes / 1440} dias`;
  if (minutes >= 60 && minutes % 60 === 0) {
    const hours = minutes / 60;
    return hours === 1 ? '1 hora' : `${hours} horas`;
  }
  return minutes === 1 ? '1 minuto' : `${minutes} minutos`;
}

/** Ramo no meio da frase: "barbearia", mas "Studio Ana" fica como está. */
function branchInline(branch: string): string {
  return /^[A-ZÀ-Ý][^A-ZÀ-Ý]*$/.test(branch)
    ? branch.charAt(0).toLowerCase() + branch.slice(1)
    : branch;
}

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

// ---------- textos de corte ----------

/** Corta no limite sem partir palavra. */
function clipWords(text: string, max: number, ellipsis = false): string {
  if (text.length <= max) return text;
  const room = ellipsis ? max - 1 : max;
  const cut = text.slice(0, room + 1);
  const space = cut.lastIndexOf(' ');
  const clipped = (space > room * 0.6 ? cut.slice(0, space) : text.slice(0, room)).replace(
    /[\s,.;:·-]+$/,
    '',
  );
  return ellipsis ? `${clipped}…` : clipped;
}

/** Primeiro candidato que cabe em 60; senão o último, cortado. Depois o sufixo, se couber. */
function fitTitle(candidates: (string | null)[]): string {
  const options = candidates.filter((c): c is string => !!c);
  const fit =
    options.find((option) => option.length <= TITLE_MAX) ??
    clipWords(options[options.length - 1], TITLE_MAX);
  return pageTitle(fit);
}

/** Junta frases até passar de 120 caracteres e corta em 160. */
function fitDescription(sentences: (string | null | false | undefined)[]): string {
  let text = '';
  for (const sentence of sentences) {
    if (!sentence) continue;
    if (text.length >= DESCRIPTION_MIN) break;
    text = text ? `${text} ${sentence}` : sentence;
  }
  return clipWords(text, DESCRIPTION_MAX, true);
}

// ---------- perfil ----------

export function profilePath(profile: Pick<MetaProfile, 'slug'>): string {
  return `/@${profile.slug}`;
}

/** "Corte por R$ 45,00", "Barba a partir de R$ 30,00", "Plano R$ 120,00 por mês". */
function serviceWithPrice(service: MetaService): string {
  const price = priceText(service);
  if (!price) return service.name;
  return price.startsWith('a partir') ? `${service.name} ${price}` : `${service.name} por ${price}`;
}

export function profileMeta(profile: MetaProfile): PageMeta {
  const name = profile.businessName.trim();
  const branch = profileBranch(profile);
  const place = profilePlace(profile);
  const services = profileServices(profile);
  const bookable = hasBookableService(services);

  const title = fitTitle([
    branch && place ? `${name} · ${branch} em ${place}` : null,
    place ? `${name} em ${place}` : null,
    branch ? `${name} · ${branch}` : null,
    name,
  ]);

  const bio = profile.bio?.replace(/\s+/g, ' ').trim() ?? '';
  const priced = services.filter((service) => visiblePrice(service)).slice(0, 2);
  const listed = (priced.length ? priced : services.slice(0, 2)).map(serviceWithPrice);
  const description =
    bio.length >= DESCRIPTION_MIN
      ? clipWords(bio, DESCRIPTION_MAX, true)
      : fitDescription([
          `${name}${branch ? `, ${branchInline(branch)}` : ''}${place ? ` em ${place}` : ''}.`,
          listed.length ? `${joinPt(listed)}.` : null,
          bio ? (/[.!?]$/.test(bio) ? bio : `${bio}.`) : null,
          bookable
            ? 'Veja os horários livres e agende online.'
            : 'Veja os serviços e os preços na página.',
          'Página na Controlapag com serviços, preços e formas de pagamento.',
        ]);

  return { title, description, path: profilePath(profile) };
}

/** Página de um serviço: título e canônica próprios. */
export function serviceMeta(profile: MetaProfile, service: MetaService): PageMeta {
  const name = profile.businessName.trim();
  const place = profilePlace(profile);
  const price = priceText(service);
  const description = fitDescription([
    service.description?.trim()
      ? /[.!?]$/.test(service.description.trim())
        ? service.description.trim()
        : `${service.description.trim()}.`
      : null,
    `${service.name} com ${name}${place ? ` em ${place}` : ''}${price ? `: ${price}` : ''}${
      durationText(service.durationMinutes) ? `, ${durationText(service.durationMinutes)}` : ''
    }.`,
    service.durationMinutes
      ? 'Veja os horários livres e agende online por esta página.'
      : 'Fale com o profissional para combinar o horário.',
    'Página na Controlapag.',
  ]);
  return {
    title: fitTitle([`${service.name} · ${name}`, service.name]),
    description,
    path: `/@${profile.slug}/service/${encodeURIComponent(service.id)}`,
  };
}

/** Os serviços que um profissional da equipe faz (do catálogo, quando existe). */
export function memberServices(profile: MetaProfile, member: MetaTeamMember): MetaService[] {
  if (profile.catalog?.length) {
    return profile.catalog.flatMap((item) => {
      const mine = item.professionals.find((p) => p.providerId === member.providerId);
      if (!mine) return [];
      return [
        {
          id: mine.serviceId,
          name: item.name,
          description: item.description ?? null,
          price: mine.price,
          priceDisplayMode: mine.priceDisplayMode,
          durationMinutes: mine.durationMinutes ?? item.durationMinutes ?? null,
          billingInterval: mine.billingInterval ?? null,
          catalogItemId: item.id,
        },
      ];
    });
  }
  return member.services;
}

/** Página de um profissional da equipe dentro da página do negócio. */
export function professionalMeta(profile: MetaProfile, member: MetaTeamMember): PageMeta {
  const business = profile.businessName.trim();
  const place = profilePlace(profile);
  const services = memberServices(profile, member);
  const listed = services.slice(0, 2).map(serviceWithPrice);
  const bio = member.bio?.replace(/\s+/g, ' ').trim() ?? '';
  return {
    title: fitTitle([`${member.name} · ${business}`, member.name]),
    description:
      bio.length >= DESCRIPTION_MIN
        ? clipWords(bio, DESCRIPTION_MAX, true)
        : fitDescription([
            `${member.name} atende em ${business}${place ? `, ${place}` : ''}.`,
            listed.length ? `${joinPt(listed)}.` : null,
            bio ? (/[.!?]$/.test(bio) ? bio : `${bio}.`) : null,
            hasBookableService(services)
              ? `Veja os horários livres e agende com ${member.name} online.`
              : 'Veja os serviços e os preços na página.',
            'Página na Controlapag.',
          ]),
    path: `/@${profile.slug}/professional/${encodeURIComponent(member.providerId)}`,
  };
}

/** O título da página que não existe é o mesmo na função e na tela. */
export const PROFILE_NOT_FOUND_TITLE = pageTitle('Perfil não encontrado');

// ---------- resposta e perguntas ----------

/**
 * Bloco de resposta logo abaixo do nome: "[Nome] é [ramo] em [cidade].
 * Atende [dias], cobra a partir de R$ X e aceita [formas]. Dá para agendar
 * online por esta página." Só com o que o perfil tem; sem dado, a frase sai.
 */
export function profileAnswer(profile: MetaProfile): string {
  const name = profile.businessName.trim();
  const branch = profileBranch(profile);
  const place = profilePlace(profile);
  const services = profileServices(profile);
  const sentences: string[] = [];

  if (branch && place) sentences.push(`${name} é ${branchInline(branch)} em ${place}.`);
  else if (branch) sentences.push(`${name} é ${branchInline(branch)}.`);
  else if (place) sentences.push(`${name} atende em ${place}.`);

  const clauses: string[] = [];
  const days = attendanceDays(profile.info?.openingHours);
  if (days) clauses.push(`atende ${days}`);
  const lowest = lowestPrice(services);
  if (lowest !== null) clauses.push(`cobra a partir de ${formatBRL(lowest)}`);
  const methods = profile.info?.paymentMethods ?? [];
  if (methods.length) clauses.push(`aceita ${paymentMethodsInline(methods)}`);
  if (clauses.length) {
    const body = joinPt(clauses);
    sentences.push(sentences.length ? `${capitalize(body)}.` : `${name} ${body}.`);
  }

  if (hasBookableService(services)) sentences.push('Dá para agendar online por esta página.');
  else if (services.length) sentences.push('Os serviços e os preços estão nesta página.');

  return sentences.join(' ');
}

/**
 * Perguntas sobre o perfil, todas respondidas com dado real. O mesmo array
 * vira a seção visível e o `FAQPage` (pergunta sem dado não entra).
 */
export function profileFaq(profile: MetaProfile): FaqItem[] {
  const name = profile.businessName.trim();
  const services = profileServices(profile);
  const bookable = hasBookableService(services);
  const phone = profile.info && profile.whatsappNumber ? formatPhone(profile.whatsappNumber) : null;
  const faq: FaqItem[] = [];

  if (bookable) {
    faq.push({
      question: `Como agendar com ${name}?`,
      answer:
        'Escolha o serviço nesta página, toque em Agendar e escolha um dos horários livres. Para confirmar, informe seu nome e telefone.',
    });
  } else if (phone) {
    faq.push({
      question: `Como agendar com ${name}?`,
      answer: `Os serviços desta página não têm agendamento online. Combine o horário pelo WhatsApp ${phone}.`,
    });
  }

  if (bookable && profile.booking) {
    const lead = profile.booking.minCancelLeadMinutes;
    faq.push({
      question: `Até quando posso desmarcar com ${name}?`,
      answer:
        lead > 0
          ? `Você desmarca ou remarca sozinho até ${leadTimeText(lead)} antes do horário. Depois desse prazo, fale direto com ${name}.`
          : `Você desmarca ou remarca sozinho até o horário marcado.`,
    });
  }

  const methods = profile.info?.paymentMethods ?? [];
  if (methods.length) {
    faq.push({
      question: `Quais formas de pagamento ${name} aceita?`,
      answer: `${name} aceita ${paymentMethodsInline(methods)}.`,
    });
  }

  const place = profilePlace(profile);
  if (place) {
    faq.push({ question: `Onde ${name} atende?`, answer: `${name} atende em ${place}.` });
  }

  return faq;
}

/** "(11) 98765-4321". */
export function formatPhone(raw: string): string {
  const d = raw.replace(/\D/g, '').replace(/^55(?=\d{10,11}$)/, '');
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return raw;
}

/** "+5511987654321" para o `telephone`. */
function e164(raw: string): string | null {
  const d = raw.replace(/\D/g, '');
  if (d.length === 10 || d.length === 11) return `+55${d}`;
  if ((d.length === 12 || d.length === 13) && d.startsWith('55')) return `+${d}`;
  return null;
}

// ---------- dados estruturados ----------

/** Subtipo do LocalBusiness só quando o ramo deixa claro; senão o genérico. */
const TYPE_BY_PACK: Record<string, string> = {
  barbearia: 'BarberShop',
  cabeleireiro: 'HairSalon',
  manicure: 'NailSalon',
  estetica: 'BeautySalon',
  fisioterapeuta: 'Physiotherapy',
  contador: 'AccountingService',
};
const TYPE_BY_CATEGORY: Record<string, string> = {
  beleza: 'HealthAndBeautyBusiness',
  consultoria: 'ProfessionalService',
};

export function businessType(profile: MetaProfile): string {
  return (
    (profile.activityPackSlug && TYPE_BY_PACK[profile.activityPackSlug]) ||
    (profile.categorySlug && TYPE_BY_CATEGORY[profile.categorySlug]) ||
    'LocalBusiness'
  );
}

const SCHEMA_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** Um item por janela de horário, com os dias que a têm. Vazio sem grade. */
export function openingHoursSpecification(intervals: OpeningInterval[] | null | undefined) {
  const byWindow = new Map<string, { opens: string; closes: string; days: number[] }>();
  for (const interval of intervals ?? []) {
    if (!/^\d{2}:\d{2}$/.test(interval.opens) || !/^\d{2}:\d{2}$/.test(interval.closes)) continue;
    if (interval.dayOfWeek < 0 || interval.dayOfWeek > 6) continue;
    const key = `${interval.opens}-${interval.closes}`;
    const entry = byWindow.get(key) ?? { opens: interval.opens, closes: interval.closes, days: [] };
    if (!entry.days.includes(interval.dayOfWeek)) entry.days.push(interval.dayOfWeek);
    byWindow.set(key, entry);
  }
  return [...byWindow.values()].map((entry) => ({
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: WEEK_ORDER.filter((day) => entry.days.includes(day)).map((day) => SCHEMA_DAYS[day]),
    opens: entry.opens,
    closes: entry.closes,
  }));
}

const UNIT_BY_INTERVAL: Record<string, { unitCode: string; quantity: number }> = {
  WEEKLY: { unitCode: 'WEE', quantity: 1 },
  MONTHLY: { unitCode: 'MON', quantity: 1 },
  BIMONTHLY: { unitCode: 'MON', quantity: 2 },
  TRIMESTERLY: { unitCode: 'MON', quantity: 3 },
  SEMIANNUALLY: { unitCode: 'MON', quantity: 6 },
  YEARLY: { unitCode: 'ANN', quantity: 1 },
};

/**
 * Oferta de um serviço. Preço exato vai em `price`; "a partir de" em
 * `priceSpecification.minPrice`; assinatura em `UnitPriceSpecification` com o
 * ciclo (`MON` = mês). Sem preço visível, a oferta sai sem preço.
 */
export function serviceOffer(service: MetaService, url?: string): Record<string, unknown> {
  const offer: Record<string, unknown> = {
    '@type': 'Offer',
    itemOffered: {
      '@type': 'Service',
      name: service.name,
      ...(service.description?.trim() ? { description: service.description.trim() } : {}),
    },
  };
  if (url) offer.url = url;
  const price = visiblePrice(service);
  if (!price) return offer;
  offer.priceCurrency = 'BRL';
  const unit = price.interval ? UNIT_BY_INTERVAL[price.interval] : undefined;
  if (unit) {
    offer.priceSpecification = {
      '@type': 'UnitPriceSpecification',
      ...(price.from ? { minPrice: price.value } : { price: price.value }),
      priceCurrency: 'BRL',
      unitCode: unit.unitCode,
      ...(unit.quantity > 1
        ? {
            referenceQuantity: {
              '@type': 'QuantitativeValue',
              value: unit.quantity,
              unitCode: unit.unitCode,
            },
          }
        : {}),
    };
  } else if (price.from) {
    offer.priceSpecification = {
      '@type': 'PriceSpecification',
      minPrice: price.value,
      priceCurrency: 'BRL',
    };
  } else {
    offer.price = price.value;
  }
  return offer;
}

/** Foto real do negócio: capa, primeira do portfólio, logo ou foto de perfil. */
export function profilePhoto(profile: MetaProfile): string | null {
  return (
    profile.coverUrl || profile.gallery?.[0]?.url || profile.logoUrl || profile.avatarUrl || null
  );
}

/** `PostalAddress` só com cidade, UF e país (D6). */
export function cityAddress(profile: MetaProfile): Record<string, unknown> | null {
  const city = profile.city?.trim();
  if (!city) return null;
  return {
    '@type': 'PostalAddress',
    addressLocality: city,
    ...(profile.state ? { addressRegion: profile.state } : {}),
    addressCountry: 'BR',
  };
}

export function businessId(url: string): string {
  return `${url}#business`;
}

/** LocalBusiness do perfil. `url` é a canônica absoluta; `fallbackImage`, a prévia. */
export function profileJsonLd(
  profile: MetaProfile,
  url: string,
  fallbackImage: string,
): Record<string, unknown> {
  const data: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': businessType(profile),
    '@id': businessId(url),
    name: profile.businessName,
    url,
    image: profilePhoto(profile) ?? fallbackImage,
  };
  if (profile.bio?.trim()) data.description = profile.bio.trim();
  // O telefone só entra quando a tela já mostra (na seção Informações).
  const telephone = profile.info && profile.whatsappNumber ? e164(profile.whatsappNumber) : null;
  if (telephone) data.telephone = telephone;
  const address = cityAddress(profile);
  if (address) data.address = address;
  const methods = profile.info?.paymentMethods ?? [];
  if (methods.length) data.paymentAccepted = methods.map(paymentMethodLabel).join(', ');
  const hours = openingHoursSpecification(profile.info?.openingHours);
  if (hours.length) data.openingHoursSpecification = hours;
  const sameAs = [
    profile.info?.socialLinks?.instagram
      ? `https://instagram.com/${profile.info.socialLinks.instagram.replace(/^@/, '')}`
      : null,
    profile.info?.socialLinks?.website ?? null,
  ].filter((link): link is string => !!link && /^https?:\/\//.test(link));
  if (sameAs.length) data.sameAs = sameAs;
  const services = profileServices(profile).slice(0, 20);
  if (services.length) data.makesOffer = services.map((service) => serviceOffer(service));
  return data;
}

/** `FAQPage` do mesmo array da seção visível; `null` sem pergunta. */
export function faqJsonLd(faq: FaqItem[]): Record<string, unknown> | null {
  if (!faq.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faq.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  };
}

/** Service da página do serviço, ligado ao negócio pelo `@id`. */
export function serviceJsonLd(
  profile: MetaProfile,
  service: MetaService,
  url: string,
  profileUrl: string,
) {
  const data: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.name,
    url,
    provider: {
      '@type': businessType(profile),
      '@id': businessId(profileUrl),
      name: profile.businessName,
      url: profileUrl,
    },
  };
  if (service.description?.trim()) data.description = service.description.trim();
  const area = cityAddress(profile);
  if (area) data.areaServed = { '@type': 'City', name: profile.city?.trim() };
  const offer = serviceOffer(service, url);
  if (visiblePrice(service)) {
    delete (offer as { itemOffered?: unknown }).itemOffered;
    data.offers = offer;
  }
  return data;
}

/** Person do profissional da equipe, que trabalha no negócio. */
export function professionalJsonLd(
  profile: MetaProfile,
  member: MetaTeamMember,
  url: string,
  profileUrl: string,
) {
  const services = memberServices(profile, member).slice(0, 20);
  const data: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: member.name,
    url,
    worksFor: {
      '@type': businessType(profile),
      '@id': businessId(profileUrl),
      name: profile.businessName,
      url: profileUrl,
    },
  };
  if (member.bio?.trim()) data.description = member.bio.trim();
  if (member.image) data.image = member.image;
  if (services.length) data.makesOffer = services.map((service) => serviceOffer(service));
  return data;
}

// ---------- diretório ----------

export interface DirectoryMetaInput {
  categoryLabel: string;
  city?: string | null;
  state?: string | null;
  /** Quantos perfis a página lista (vem da API). */
  providers?: number | null;
}

/** Título, descrição e H1 do diretório; a função e a tela usam a mesma. */
export function directoryMeta(entry: DirectoryMetaInput): {
  title: string;
  description: string;
  h1: string;
} {
  const label = entry.categoryLabel.trim();
  const place = entry.city ? `${entry.city} (${entry.state ?? ''})`.replace(' ()', '') : null;
  const where = place ? ` em ${place}` : '';
  const count = entry.providers && entry.providers > 0 ? entry.providers : null;
  const who = count
    ? `${count} ${count === 1 ? 'profissional' : 'profissionais'} de ${branchInline(label)}${where}`
    : `Profissionais de ${branchInline(label)}${where}`;
  return {
    title: fitTitle([`${label}${where} · agende online`, `${label}${where}`]),
    description: fitDescription([
      `${who} com preço, serviços e horários livres.`,
      'Agende direto pela página de cada um.',
      'Lista de perfis públicos na Controlapag, com cidade e preço de cada um.',
    ]),
    h1: place ? `${label} em ${place}` : `${label}: profissionais para agendar online`,
  };
}
